import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import client from "@/lib/api";
import {
  Search, Briefcase, Home, Building2, Users, ClipboardList,
  AlertTriangle, MessageSquare, Loader2, ArrowRight,
} from "lucide-react";

export const SEARCH_CATS = {
  clients: { icon: Briefcase, route: "/clients" },
  properties: { icon: Home, route: "/properties" },
  sites: { icon: Building2, route: "/sites" },
  staff: { icon: Users, route: "/staff" },
  jobs: { icon: ClipboardList, route: "/jobs" },
  issues: { icon: AlertTriangle, route: "/issues" },
  messages: { icon: MessageSquare, route: "/messages" },
};
const CAT_ORDER = ["clients", "properties", "sites", "staff", "jobs", "issues", "messages"];

export function GlobalSearch() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const run = useCallback(async (query) => {
    if (query.trim().length < 2 && !/^\d$/.test(query.trim())) {
      setGroups({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.get("/search", { params: { q: query, limit: 5 } });
      setGroups(data.groups || {});
    } catch {
      setGroups({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => run(q), 250);
    return () => clearTimeout(id);
  }, [q, run]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goAll = () => {
    if (q.trim().length < 2 && !/^\d$/.test(q.trim())) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const pick = (cat, r) => {
    setOpen(false);
    navigate(cat === "jobs" && r ? `/jobs/${r.id}` : SEARCH_CATS[cat]?.route || "/");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") goAll();
    if (e.key === "Escape") setOpen(false);
  };

  const cats = CAT_ORDER.filter((c) => groups[c]?.length);
  const total = cats.reduce((n, c) => n + groups[c].length, 0);
  const showPanel = open && q.trim().length >= 1;

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-slate-100 border border-transparent focus-within:bg-white focus-within:border-brand/40 focus-within:ring-2 focus-within:ring-brand/15 transition-all">
        <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t("search.placeholder", "Search iClean…")}
          className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
        />
        {loading && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
      </div>

      {showPanel && (
        <div
          data-testid="global-search-panel"
          className="absolute top-12 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 max-h-[70vh] overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {q.trim().length < 2 && !/^\d$/.test(q.trim()) ? (
            <p className="px-4 py-4 text-sm text-slate-400" data-testid="global-search-hint">{t("search.hint", "Type at least 2 characters…")}</p>
          ) : loading ? (
            <p className="px-4 py-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("search.searching", "Searching…")}</p>
          ) : total === 0 ? (
            <p className="px-4 py-4 text-sm text-slate-400" data-testid="global-search-empty">{t("search.noResults", "No results found.")}</p>
          ) : (
            <div className="py-1">
              {cats.map((cat) => {
                const Icon = SEARCH_CATS[cat].icon;
                return (
                  <div key={cat} data-testid={`global-search-group-${cat}`}>
                    <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5" /> {t(`search.cat.${cat}`, cat)}
                    </div>
                    {groups[cat].map((r) => (
                      <button
                        key={`${cat}-${r.id}`}
                        data-testid={`global-search-result-${cat}-${r.id}`}
                        onClick={() => pick(cat, r)}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 transition flex flex-col"
                      >
                        <span className="text-sm text-slate-800 font-medium truncate">{r.title || "—"}</span>
                        {r.sub && <span className="text-xs text-slate-400 truncate">{r.sub}</span>}
                      </button>
                    ))}
                  </div>
                );
              })}
              <button
                data-testid="global-search-view-all"
                onClick={goAll}
                className="w-full mt-1 border-t border-slate-100 px-4 py-2.5 text-sm font-semibold text-brand hover:bg-brand/5 transition flex items-center justify-between"
              >
                <span>{t("search.viewAll", "View all results")}</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
